import { computed, IComputedValue } from 'mobx';
import {
    getDayWeekItemConfig,
    getMonthItemConfig,
    monthDayCellConfig,
} from './OwaCalendarItemConfig';
import {
    onSurfaceClick,
    onSurfaceDoubleClick,
    onSurfaceRightClick,
} from '../actions/surfaceOnClickActions';
import { getCalendarFolderId } from 'owa-calendar-cache';
import { getCalendarColorScheme, getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { unselectCalendar } from 'owa-calendar-surface-select-calendar';
import { surfaceStore } from 'owa-calendar-surface-surface-store';
import { getSelectedCalendars } from 'owa-calendar-surface-calendars-store';
import { isPreviewItemId } from 'owa-quick-compose-controller';
import { CalendarEvent, compareCalendarEvents } from 'owa-calendar-types';
import { SearchScenarioId, getScenarioStore } from 'owa-search-store';
import { dismissQuickComposeIfNotDirty } from 'owa-calendar-compose-form-actions';
import type { DragData } from 'owa-dnd/lib/utils/dragDataUtil';
import { lazyShowMeetingDndModal } from 'owa-calendar-modal-actions';
import { WeatherSurfaceItem, isWeatherAvailableForDate } from 'owa-weather';
import { getAllDayWellHeight, getNumDaysInDayRange } from 'owa-calendar-surface-options-utils';
import {
    shouldShowWeekMonthName,
    shouldShowWeekDayTemp,
    shouldShowWeekWeatherIcon,
    shouldShowDynamicWeekColumns,
    shouldShowFullWeekDayName,
    CalendarLayoutSplitViewConstants,
} from 'owa-calendar-layout';
import * as React from 'react';
import {
    CalendarSurfaceTransformProps,
    LinearItemsLayout,
    DatesColumnProvider,
    getDynamicColumnWidthRatios,
    getDefaultColumnWidthRatios,
    contentHeaderTemplate,
} from 'owa-calendar-surface-dategrid';
import type {
    TypedCalendarDayContentConfig,
    CalendarSurfaceConfig,
    CalendarMonthConfig,
    FocusableSurface,
} from 'owa-calendar-surface-core';
import {
    CalendarMonthTransformProps,
    createCalendarDayContent,
    getCalendarMonthContent,
    updateFocusedSurfaceElement,
    getSurfaceItems,
} from 'owa-calendar-surface';
import {
    canDragItem,
    canDropItemType,
    getSurfaceItemForPreview,
    onDragStart,
    onDragEnd,
    onItemDropped,
} from 'owa-calendar-surface-dategrid-common-dnd-callbacks';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';
import { containsText } from 'owa-calendar-event-capabilities';
import InDayComponent, { InDayAdditionalPropsType } from '../components/InDayComponent';
import DayGridAllDayComponent, {
    DayGridAllDayAdditionalProps,
} from 'owa-day-grid/lib/components/DayGridAllDayComponent';
import DayGridAllDayHeaderComponent, {
    DayGridAllDayHeaderAdditionalProps,
} from 'owa-day-grid/lib/components/DayGridAllDayHeaderComponent';
import { dayGridFreeBusyTemplate } from 'owa-day-grid/lib/components/DayGridTemplates';
import DayGridInDayContentComponent, {
    DayGridInDayContentAdditionalProps,
} from 'owa-day-grid/lib/components/DayGridInDayContentComponent';
import MonthSurfaceComponent, {
    MonthSurfaceAdditionalPropsType,
} from '../components/MonthSurfaceComponent';
import { getCalendarFolderWorkingHours } from 'owa-calendar-events-store';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { eventFilter, getDefaultCurrentFilters } from 'owa-calendar-events-filter';
import {
    isCalendarQuickComposeShown,
    getFormIdForCurrentCommand,
} from 'owa-calendar-module-command';

/**
 * Properties for generating day/week content
 */
export let surfaceDayContentConfig: CalendarSurfaceTransformProps = {
    onSurfaceClick: onSurfaceClick,
    onSurfaceDoubleClick: onSurfaceDoubleClick,
    onSurfaceRightClick: onSurfaceRightClick,
    columnProvider: new DatesColumnProvider(surfaceStore, models =>
        // This check MUST be in a callback; feature flags are not yet loaded when we create the config.
        shouldShowDynamicWeekColumns()
            ? getDynamicColumnWidthRatios(models)
            : getDefaultColumnWidthRatios(models)
    ),
    itemsLayout: new LinearItemsLayout(surfaceStore),
    renderDateHeaderAddOn: date =>
        isHostAppFeatureEnabled('weekWeatherIcon') &&
        shouldShowWeekWeatherIcon() &&
        isWeatherAvailableForDate(date) ? (
            <WeatherSurfaceItem
                date={date}
                showInTopRight={true}
                shouldShowWeekDayTemp={shouldShowWeekDayTemp}
            />
        ) : undefined,
    dragDropConfig: {
        canDrag: canDragItem,
        canDrop: canDropItemType,
        onItemDropped: handleItemDropped,
        onDragStart: onDragStart,
        onDragEnd: onDragEnd,
        getSurfaceItemForPreview: getSurfaceItemForPreview,
        colorProvider: getCalendarEventColorScheme,
    },
    getItemConfig: getDayWeekItemConfig,
    freeBusyTemplate: dayGridFreeBusyTemplate,
    supportExpandableAllDayWell: () => true,
    shouldShowCurrentTime: () => true,
    shouldShowWeekMonthName: shouldShowWeekMonthName,
    timeStripConfig: {
        highlightCurrentTime: true,
    },
};

/**
 * Properties for generating month content
 */
let surfaceMonthContentConfig: CalendarMonthTransformProps = {
    monthDayCellsConfig: monthDayCellConfig,
    renderDateHeaderAddOn: date =>
        isHostAppFeatureEnabled('weekWeatherIcon') &&
        shouldShowWeekWeatherIcon() &&
        isWeatherAvailableForDate(date) ? (
            <WeatherSurfaceItem
                date={date}
                showInTopRight={false}
                shouldShowWeekDayTemp={shouldShowWeekDayTemp}
            />
        ) : undefined,
    dragDropConfig: {
        canDrag: canDragItem,
        canDrop: canDropItemType,
        onItemDropped: onItemDropped,
        onDragStart: onDragStart,
        onDragEnd: onDragEnd,
        getSurfaceItemForPreview: getSurfaceItemForPreview,
        colorProvider: getCalendarEventColorScheme,
    },
    getItemConfig: getMonthItemConfig,
    shouldShowWeekMonthName: shouldShowWeekMonthName,
    shouldShowFullWeekDayName: shouldShowFullWeekDayName,
};

/**
 * Properties for Calendar Surface core controls: Day/Week and Month
 */

let surfaceConfig: CalendarSurfaceConfig = {
    dayViewConfig: {
        supportSplitView: () => {
            return surfaceStore.isInSplitView;
        },
        // TODO: WI 97881 Remove minWidthProvider() in 1 and 2 day view split view scenarios
        minWidthProvider: () =>
            surfaceStore.isInSplitView &&
            surfaceStore.dateRangeType == DateRangeType.Day &&
            getNumDaysInDayRange() <= 2
                ? CalendarLayoutSplitViewConstants.selectedCalendarMinWidth_DAY
                : undefined,
        supportExpandableAllDayWell: surfaceDayContentConfig.supportExpandableAllDayWell,
        allDayWellCollapsedHeightProvider: () => getAllDayWellHeight(),
        setFocusedElement: setFocusedElement,
        calendarDayContentConfigProvider: () => getCalendarDayContentConfigs.get(),
        onSurfaceScroll: () => {
            if (isCalendarQuickComposeShown()) {
                dismissQuickComposeIfNotDirty(getFormIdForCurrentCommand());
            }
        },
    },
    monthViewConfig: getCalendarMonthViewConfig(),
};

function getCalendarMonthViewConfig(): CalendarMonthConfig<MonthSurfaceAdditionalPropsType> {
    return {
        contentGenerator: getMonthContent,
        setFocusedElement: setFocusedElement,
        monthSurfaceComponent: {
            component: MonthSurfaceComponent,
            additionalProps: {
                surfaceStore: surfaceStore,
                calendarConfig: surfaceDayContentConfig,
            },
        },
    };
}

/**
 * Computed to avoid functional reference changing unless store values change
 */
const getCalendarDayContentConfigs = computed(
    function getCalendarDayContentConfigs(): TypedCalendarDayContentConfig<
        DayGridAllDayAdditionalProps,
        DayGridAllDayHeaderAdditionalProps,
        InDayAdditionalPropsType,
        DayGridInDayContentAdditionalProps
    >[] {
        return surfaceStore.isInSplitView
            ? getSplitViewCalendarDayContentConfigs()
            : getMergedViewCalendarDayContentConfigs();
    }
);

function getSplitViewCalendarDayContentConfigs() {
    const searchText = getCalendarSearchTextComputed.get();

    return getSelectedCalendars().map((calendar, index) => {
        let calendarFolderId = getCalendarFolderId(calendar);
        let calendarWorkingHours = getCalendarFolderWorkingHours(calendarFolderId);
        let colorScheme =
            calendar.colorScheme == null
                ? getCalendarColorScheme(calendar.calendarId)
                : calendar.colorScheme;

        const calendarDayContent = createCalendarDayContent(
            surfaceStore,
            surfaceDayContentConfig,
            () => {
                let itemsPerCalendar = filterClientItems(searchText);
                return itemsPerCalendar.filter(item => item.ParentFolderId.Id == calendarFolderId);
            },
            calendarFolderId,
            customEventsComparator,
            undefined,
            undefined,
            calendarWorkingHours
        );

        return {
            content: calendarDayContent,
            contentDescription: () =>
                contentHeaderTemplate(index.toString(), calendar.CalendarName, colorScheme, () =>
                    unselectCalendar(calendar)
                ),
            colorScheme: colorScheme,
            allDayComponent: {
                component: DayGridAllDayComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                    calendarDayContent: calendarDayContent,
                },
            },
            allDayHeaderComponent: {
                component: DayGridAllDayHeaderComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
            inDayComponent: {
                component: InDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                },
            },
            inDayContentComponent: {
                component: DayGridInDayContentComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarFolderId: calendarFolderId,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
        };
    });
}

function getMergedViewCalendarDayContentConfigs() {
    const searchText = getCalendarSearchTextComputed.get();
    const calendarDayContent = createCalendarDayContent(
        surfaceStore,
        surfaceDayContentConfig,
        () => {
            return filterClientItems(searchText);
        },
        undefined,
        customEventsComparator
    );

    return [
        {
            content: calendarDayContent,
            allDayComponent: {
                component: DayGridAllDayComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarFolderId: undefined,
                    calendarConfig: surfaceDayContentConfig,
                    calendarDayContent: calendarDayContent,
                },
            },
            allDayHeaderComponent: {
                component: DayGridAllDayHeaderComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarFolderId: undefined,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
            inDayComponent: {
                component: InDayComponent,
                additionalProps: {
                    surfaceStore: surfaceStore,
                },
            },
            inDayContentComponent: {
                component: DayGridInDayContentComponent,
                additionalProps: {
                    viewState: surfaceStore,
                    calendarConfig: surfaceDayContentConfig,
                },
            },
        },
    ];
}

function getMonthContent() {
    return getCalendarMonthContent(
        surfaceStore,
        surfaceMonthContentConfig,
        getCalendarSearchTextComputed.get(),
        false,
        getSurfaceItems(surfaceStore).filter(item => eventFilter(item, getDefaultCurrentFilters()))
    );
}

function setFocusedElement(id: string) {
    updateFocusedSurfaceElement(id, surfaceStore);
}

function isClientSideFilteringEnabled(searchText: string) {
    return searchText && searchText.length > 0;
}

function filterClientItems(searchText: string) {
    let items = getSurfaceItems(surfaceStore);
    if (isClientSideFilteringEnabled(searchText)) {
        items = items.filter(
            item => isPreviewItemId(item.ItemId.Id) || containsText(item, searchText)
        );
    }
    items = items.filter(item => eventFilter(item, getDefaultCurrentFilters()));
    return items;
}

function customEventsComparator(item1: CalendarEvent, item2: CalendarEvent): number {
    // For all day events, move the event to the front if it is highlighted in the grid
    if (isPreviewItemId(item1.ItemId.Id)) {
        return -1;
    } else if (isPreviewItemId(item2.ItemId.Id)) {
        return 1;
    }

    // Apply default sorting for non-all day or non-highlighted events
    return compareCalendarEvents(item1, item2);
}

function handleItemDropped(
    droppedItem: CalendarEvent,
    originalItem: CalendarEvent,
    dragInfo: DragData
) {
    onItemDropped(
        droppedItem,
        originalItem,
        dragInfo,
        (calendarItemUpdates: Partial<CalendarEvent>) => {
            lazyShowMeetingDndModal.importAndExecute(calendarItemUpdates, originalItem);
        }
    );
}

let surfaceComponent: FocusableSurface;
surfaceConfig.surfaceComponent = surface => {
    surfaceComponent = surface;
};

const getCalendarSearchTextComputed: IComputedValue<string> = computed(() => {
    return getScenarioStore(SearchScenarioId.Calendar).searchText;
});

surfaceConfig.dayViewConfig.surfaceComponent = surfaceConfig.surfaceComponent;
surfaceConfig.monthViewConfig.surfaceComponent = surfaceConfig.surfaceComponent;

export function getFocusableSurfaceComponent() {
    return surfaceComponent;
}

export default surfaceConfig;
